import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useUserSettings } from "@/contexts"
import type { CurrencyCode } from "@/models/currencyCode"
import type { SelectableLanguageCode } from "@/models/i18n"
import type { UnitName } from "@/models/unit"
import {
  defaultCurrencyFormatOptions,
  isDecimalEmpty,
  translateDecimal,
  withYenWholeNumberFormat,
} from "@/utils/decimal"
import type { DecimalFormatOptions, DecimalOptions } from "@/utils/decimal"

export interface FormatCurrencyCallOptions {
  currency?: string | null
  /** Fraction digits for this call. Zero is a real length. Yen still wins. */
  precision?: number
  showPlaceholder?: boolean
  /** A catalog unit, including a `-per-` form. The rate number is not converted. */
  unit?: UnitName
}

export type FormatCurrency = (
  value: string | number | null | undefined,
  options?: FormatCurrencyCallOptions
) => string

/**
 * Formats a value as currency.
 * @param formatOptions - The Intl.NumberFormat options to use.
 * @param options - Additional options for formatting.
 * @returns - The localized formatted currency string.
 */
export const useFormatCurrency = (
  formatOptions: DecimalFormatOptions = {},
  options: DecimalOptions = {}
) => {
  const { i18n } = useTranslation()
  const { currency } = useUserSettings()

  const formatCurrency = useCallback(
    (
      value: string | number | null | undefined,
      callOptions?: FormatCurrencyCallOptions
    ) => {
      const explicitCurrency = callOptions?.currency?.trim()
      const callPrecision = callOptions?.precision

      if (callOptions?.showPlaceholder === false && isDecimalEmpty(value)) {
        return ""
      }

      return translateDecimal({
        format: withYenWholeNumberFormat({
          currency,
          ...defaultCurrencyFormatOptions,
          ...formatOptions,
          ...(explicitCurrency
            ? { currency: explicitCurrency as CurrencyCode }
            : {}),
          ...(callPrecision != null ? { precision: callPrecision } : {}),
        }),
        localeCode: i18n.language as SelectableLanguageCode,
        options: {
          ...options,
          ...(callOptions?.unit != null ? { unit: callOptions.unit } : {}),
        },
        value,
      })
    },
    [i18n.language, currency, formatOptions, options]
  )

  return useMemo(() => ({ formatCurrency }), [formatCurrency])
}
